(function (root, document) {
  'use strict';

  var STORAGE_FIRST = 'p4m:funnel:first-touch';
  var STORAGE_LAST = 'p4m:funnel:last-touch';
  var STORAGE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  var CART_SYNC_FINGERPRINT = 'p4m:funnel:cart-attributes';
  var EVENT_PREFIX = 'p4m_';
  var lastCartSignal = '';
  var lastCartSignalAt = 0;
  var lastCheckoutSignalAt = 0;
  var productViewTracked = false;
  var cartViewTracked = false;
  var cartSyncPromise = null;

  function cleanToken(value, fallback) {
    var cleaned = String(value || '')
      .replace(/[^a-z0-9._ -]+/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80);
    return cleaned || fallback || '';
  }

  function cleanPath(value) {
    var path = String(value || '').split('?')[0].split('#')[0];
    if (!path.startsWith('/')) return '/';
    return path.slice(0, 180);
  }

  function cleanClickId(value) {
    return String(value || '')
      .replace(/[^a-z0-9._~-]+/gi, '')
      .slice(0, 180);
  }

  function sourceFamily(value) {
    var source = cleanToken(value, 'direct').toLowerCase();
    if (/^(?:chatgpt(?:\.com)?|chat\.openai\.com|openai(?:\.com)?)$/.test(source)) return 'chatgpt';
    if (/^(?:google|google\.com)$/.test(source)) return 'google';
    if (/^(?:instagram|instagram\.com|ig)$/.test(source)) return 'instagram';
    if (/^(?:facebook|facebook\.com|fb)$/.test(source)) return 'facebook';
    return source;
  }

  function safeHost(value) {
    try {
      return new URL(value, root.location.origin).hostname.toLowerCase().replace(/^www\./, '').slice(0, 100);
    } catch (error) {
      return '';
    }
  }

  function isExternalReferrer(host) {
    var currentHost = String(root.location.hostname || '').toLowerCase().replace(/^www\./, '');
    return Boolean(host && host !== currentHost);
  }

  function currentTouch() {
    var params = new URLSearchParams(root.location.search || '');
    var referrerHost = safeHost(document.referrer);
    var externalReferrer = isExternalReferrer(referrerHost);
    var explicitSource = params.get('utm_source') || '';
    var gclid = cleanClickId(params.get('gclid'));
    var gbraid = cleanClickId(params.get('gbraid'));
    var wbraid = cleanClickId(params.get('wbraid'));
    var googleClick = Boolean(gclid || gbraid || wbraid);
    var inferredSource = explicitSource || (googleClick ? 'google' : (externalReferrer ? referrerHost : ''));
    return {
      source: sourceFamily(inferredSource || 'direct'),
      medium: cleanToken(params.get('utm_medium'), googleClick ? 'cpc' : (explicitSource ? 'campaign' : (externalReferrer ? 'referral' : 'none'))),
      campaign: cleanToken(params.get('utm_campaign'), 'none'),
      content: cleanToken(params.get('utm_content')),
      term: cleanToken(params.get('utm_term')),
      gclid: gclid,
      gbraid: gbraid,
      wbraid: wbraid,
      referrerHost: referrerHost || 'none',
      landingPath: cleanPath(root.location.pathname),
      capturedAt: new Date().toISOString()
    };
  }

  function readJson(key) {
    try {
      var raw = root.localStorage.getItem(key) || root.sessionStorage.getItem(key);
      var value = JSON.parse(raw || 'null');
      var capturedAt = Date.parse(value && value.capturedAt || '');
      if (capturedAt && Date.now() - capturedAt > STORAGE_MAX_AGE_MS) {
        root.localStorage.removeItem(key);
        root.sessionStorage.removeItem(key);
        return null;
      }
      return value;
    } catch (error) {
      return null;
    }
  }

  function writeJson(key, value) {
    try {
      root.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      try {
        root.sessionStorage.setItem(key, JSON.stringify(value));
      } catch (fallbackError) {
        // Attribution must never interrupt shopping if storage is unavailable.
      }
    }
  }

  function attribution() {
    var touch = currentTouch();
    var first = readJson(STORAGE_FIRST);
    var last = readJson(STORAGE_LAST);
    var hasNewSource = touch.source !== 'direct' || touch.campaign !== 'none' || touch.gclid || touch.gbraid || touch.wbraid;

    if (!first) {
      first = touch;
      writeJson(STORAGE_FIRST, first);
    }
    if (!last || hasNewSource) {
      last = touch;
      writeJson(STORAGE_LAST, last);
    }

    return {
      first: first,
      last: last || first,
      source: (last || first).source,
      medium: (last || first).medium,
      campaign: (last || first).campaign
    };
  }

  function compactAttribute(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 180);
  }

  function cartAttributionAttributes(touch) {
    var first = touch.first || {};
    var last = touch.last || first;
    return {
      _p4m_attr_v: '1',
      _p4m_ft_source: compactAttribute(first.source),
      _p4m_ft_medium: compactAttribute(first.medium),
      _p4m_ft_campaign: compactAttribute(first.campaign),
      _p4m_ft_content: compactAttribute(first.content),
      _p4m_ft_term: compactAttribute(first.term),
      _p4m_ft_landing: compactAttribute(first.landingPath),
      _p4m_ft_captured_at: compactAttribute(first.capturedAt),
      _p4m_ft_gclid: cleanClickId(first.gclid),
      _p4m_ft_gbraid: cleanClickId(first.gbraid),
      _p4m_ft_wbraid: cleanClickId(first.wbraid),
      _p4m_lt_source: compactAttribute(last.source),
      _p4m_lt_medium: compactAttribute(last.medium),
      _p4m_lt_campaign: compactAttribute(last.campaign),
      _p4m_lt_content: compactAttribute(last.content),
      _p4m_lt_term: compactAttribute(last.term),
      _p4m_lt_landing: compactAttribute(last.landingPath),
      _p4m_lt_captured_at: compactAttribute(last.capturedAt),
      _p4m_lt_gclid: cleanClickId(last.gclid),
      _p4m_lt_gbraid: cleanClickId(last.gbraid),
      _p4m_lt_wbraid: cleanClickId(last.wbraid)
    };
  }

  function syncCartAttribution(force) {
    var attributes = cartAttributionAttributes(attribution());
    var fingerprint = JSON.stringify(attributes);
    try {
      if (!force && root.sessionStorage.getItem(CART_SYNC_FINGERPRINT) === fingerprint) {
        return Promise.resolve(true);
      }
    } catch (error) {
      // A missing session store should not block the cart update.
    }

    if (cartSyncPromise && !force) return cartSyncPromise;
    cartSyncPromise = fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) return false;
      try {
        root.sessionStorage.setItem(CART_SYNC_FINGERPRINT, fingerprint);
      } catch (error) {
        // The order attributes are still persisted when session storage is unavailable.
      }
      return true;
    }).catch(function () {
      return false;
    }).finally(function () {
      cartSyncPromise = null;
    });
    return cartSyncPromise;
  }

  function deviceType() {
    return root.matchMedia && root.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
  }

  function sessionClass() {
    var params = new URLSearchParams(root.location.search || '');
    try {
      if (params.get('p4m_qa') === '1') root.sessionStorage.setItem('p4m:session-class', 'qa');
      return root.sessionStorage.getItem('p4m:session-class') === 'qa' ? 'qa' : 'commercial';
    } catch (error) {
      return params.get('p4m_qa') === '1' ? 'qa' : 'commercial';
    }
  }

  function applySessionTags() {
    if (typeof root.clarity !== 'function') return;
    root.clarity('set', 'p4m_session_class', sessionClass());
  }

  function safeDetail(detail) {
    var value = detail || {};
    return {
      product_handle: cleanToken(value.product_handle),
      product_id: cleanToken(value.product_id),
      variant_id: cleanToken(value.variant_id),
      interaction: cleanToken(value.interaction),
      quantity: Math.max(0, Math.min(99, Number(value.quantity) || 0)),
      landing_path: cleanPath(value.landing_path || root.location.pathname)
    };
  }

  function publish(name, detail) {
    var eventName = cleanToken(name).toLowerCase();
    if (!eventName.startsWith(EVENT_PREFIX)) return;

    var touch = attribution();
    var payload = Object.assign({
      event: eventName,
      p4m_source: touch.source,
      p4m_medium: touch.medium,
      p4m_campaign: touch.campaign,
      p4m_first_source: touch.first.source,
      p4m_first_campaign: touch.first.campaign,
      p4m_device: deviceType(),
      p4m_session_class: sessionClass()
    }, safeDetail(detail));

    root.dataLayer = root.dataLayer || [];
    root.dataLayer.push(payload);

    if (root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function') {
      root.Shopify.analytics.publish(eventName, payload);
    }
    if (typeof root.clarity === 'function') {
      root.clarity('set', 'p4m_source', touch.source);
      root.clarity('set', 'p4m_campaign', touch.campaign);
      root.clarity('set', 'p4m_session_class', sessionClass());
      if (payload.interaction) root.clarity('set', 'p4m_interaction', payload.interaction);
      root.clarity('event', eventName);
    }
  }

  function productContext(node) {
    var rootNode = node || document.querySelector('[data-p4m-product-handle]');
    if (!rootNode) return null;
    var form = rootNode.querySelector('product-form-component');
    var variantInput = form && form.querySelector('input[name="id"]');
    return {
      product_handle: rootNode.getAttribute('data-p4m-product-handle') || '',
      product_id: rootNode.getAttribute('data-p4m-product-id') || (form && form.dataset.productId) || '',
      variant_id: (variantInput && variantInput.value) || rootNode.getAttribute('data-p4m-product-variant-id') || '',
      landing_path: root.location.pathname
    };
  }

  function trackProductView() {
    var context = productContext();
    if (!context || !context.product_handle || productViewTracked) return;
    productViewTracked = true;
    publish('p4m_product_viewed', context);
  }

  function trackCartView() {
    if (cartViewTracked || !/^\/cart\/?$/.test(root.location.pathname || '')) return;
    cartViewTracked = true;
    publish('p4m_cart_viewed', {});
  }

  async function confirmCartAdd(event) {
    var component = event.target && event.target.closest && event.target.closest('product-form-component');
    if (!component) return;
    var detail = event.detail && (event.detail.data || event.detail);
    if (detail && detail.didError) return;

    var context = productContext(component.closest('[data-p4m-product-handle]'));
    if (!context || !context.variant_id) return;
    var signal = context.variant_id + ':' + (detail && detail.itemCount || 1);
    var now = Date.now();
    if (signal === lastCartSignal && now - lastCartSignalAt < 1200) return;

    try {
      var cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { accept: 'application/json' }
      });
      if (!cartResponse.ok) return;
      var cart = await cartResponse.json();
      var present = Array.isArray(cart.items) && cart.items.some(function (item) {
        return String(item.id) === String(context.variant_id);
      });
      if (!present) return;
    } catch (error) {
      return;
    }

    lastCartSignal = signal;
    lastCartSignalAt = now;
    context.quantity = Number(detail && detail.itemCount) || 1;
    syncCartAttribution(true);
    publish('p4m_product_added_to_cart', context);
  }

  function trackCheckoutClick() {
    var now = Date.now();
    if (now - lastCheckoutSignalAt < 1500) return;
    lastCheckoutSignalAt = now;
    syncCartAttribution(true);
    publish('p4m_checkout_clicked', productContext() || {});
  }

  function productHandleFromUrl(value) {
    try {
      var pathname = new URL(value, root.location.origin).pathname;
      var match = pathname.match(/^\/products\/([^/?#]+)/);
      return match ? cleanToken(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function install() {
    attribution();
    syncCartAttribution(false);
    applySessionTags();
    trackProductView();
    trackCartView();

    document.addEventListener('cart:update', function (event) {
      confirmCartAdd(event);
    });

    document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest && event.target.closest(
        'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-p4m-checkout]'
      );
      if (target) trackCheckoutClick();

      var fitmentHelp = event.target && event.target.closest && event.target.closest('[data-p4m-fitment-help]');
      if (fitmentHelp) {
        publish('p4m_fitment_help_opened', Object.assign(productContext() || {}, {
          interaction: 'pdp_fitment_help'
        }));
      }

      var productLink = event.target && event.target.closest && event.target.closest('a[href*="/products/"]');
      if (productLink && root.location.pathname === '/search') {
        publish('p4m_search_result_opened', {
          product_handle: productHandleFromUrl(productLink.href),
          landing_path: root.location.pathname
        });
      }
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      var submitter = event.submitter;
      if (
        form && form.matches && (
          /\/checkout(?:\?|$)/.test(form.action || '') ||
          (submitter && submitter.name === 'checkout')
        )
      ) {
        trackCheckoutClick();
      }
    }, true);
  }

  root.P4MFunnel = {
    sourceFamily: sourceFamily,
    currentTouch: currentTouch,
    attribution: attribution,
    cartAttributionAttributes: cartAttributionAttributes,
    syncCartAttribution: syncCartAttribution,
    publish: publish,
    sessionClass: sessionClass
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    install();
  }
})(window, document);
